import React, { useState, useEffect, useRef, useCallback } from 'react'
import VocabularyPrompt from './components/VocabularyPrompt'
import PronunciationPrompt from './components/PronunciationPrompt'
import CompletionPanel from './components/CompletionPanel'
import remoteLog from '../../shared/remoteLog'
import * as store from './vocabularyStore'
import './VocabularyLearning.css'

export default function VocabularyLearning({ activityId, sessionId, railsEnv, onComplete }) {
  const [isLoading, setIsLoading] = useState(true)
  const [state, setState] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
    if (state) store.save(sessionId, state)
  }, [state, sessionId])

  useEffect(() => {
    remoteLog.info('VocabularyLearning MOUNTED', { activityId, sessionId, railsEnv })
    return () => remoteLog.info('VocabularyLearning UNMOUNTED', { sessionId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false

    const initialize = async () => {
      const cached = store.load(sessionId)
      if (cached && cached.items?.length > 0) {
        if (cancelled) return
        setState(cached)
        setIsLoading(false)
        return
      }

      try {
        const data = await store.fetchRandom(sessionId)
        if (cancelled) return
        const fresh = store.hydrateFromServer(sessionId, data)
        setState(fresh)
        setIsLoading(false)
      } catch (err) {
        remoteLog.error('VocabularyLearning: load error', { error: err.message })
        if (!cancelled) {
          setError('Không thể tải bài học. Vui lòng thử lại.')
          setIsLoading(false)
        }
      }
    }

    initialize()
    return () => {
      cancelled = true
    }
  }, [sessionId])

  const currentKey = store.currentItemKey(state)

  const handleSubmit = useCallback(async (answer) => {
    if (!currentKey) return { correct: false }
    setSubmitting(true)
    try {
      const result = await store.submitAnswer(sessionId, currentKey, answer)
      setState((prev) => (prev ? store.applyServerResult(prev, currentKey, result) : prev))
      return result
    } finally {
      setSubmitting(false)
    }
  }, [sessionId, currentKey])

  const handleUseHint = useCallback(async () => {
    if (!currentKey) return null
    setSubmitting(true)
    try {
      const result = await store.requestHint(sessionId, currentKey)
      setState((prev) => (prev ? store.markHintUsed(prev, currentKey) : prev))
      return result
    } finally {
      setSubmitting(false)
    }
  }, [sessionId, currentKey])

  const handleNext = useCallback(() => {
    setState((current) => {
      if (!current || !currentKey) return current
      return store.advanceQueue(current, currentKey)
    })
  }, [currentKey])

  const handleComplete = useCallback(() => {
    const current = stateRef.current
    if (onComplete) {
      onComplete({
        total_score: current ? store.totalScore(current) : 0,
        completed_items: current ? store.completedItems(current) : [],
        mastered_item_keys: current ? store.masteredItemKeys(current) : []
      })
    }
    store.clear(sessionId)
  }, [onComplete, sessionId])

  // Test helper for Cucumber / integration tests.
  useEffect(() => {
    if (typeof window === 'undefined' || railsEnv !== 'test') return undefined

    window.__VOCABULARY_TEST_HELPER__ = {
      submitAnswer: (text) => handleSubmit(text),
      useHint: () => handleUseHint(),
      next: () => handleNext(),
      getCurrentItem: () => store.currentItem(stateRef.current),
      getQueue: () => stateRef.current?.queue ?? [],
      getTotalScore: () => (stateRef.current ? store.totalScore(stateRef.current) : 0),
      getTargetScore: () => stateRef.current?.targetScore || 0
    }

    return () => {
      delete window.__VOCABULARY_TEST_HELPER__
    }
  }, [railsEnv, handleSubmit, handleUseHint, handleNext])

  if (isLoading) {
    return (
      <div className="vocabulary-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </div>
        <p className="mt-3">Đang tải bài học...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="vocabulary-error">
        <div className="alert alert-danger" role="alert">{error}</div>
      </div>
    )
  }

  const totalScore = state ? store.totalScore(state) : 0

  if (state && store.isCompleted(state)) {
    return <CompletionPanel totalScore={totalScore} onComplete={handleComplete} />
  }

  const item = store.currentItem(state)

  if (!item) {
    return <CompletionPanel totalScore={totalScore} onComplete={handleComplete} />
  }

  // Unique key per (item-key, remaining-queue-length) so a re-queued
  // item gets a fresh Prompt instance the next time it comes up: no
  // auto-reveal, kid must type from memory.
  const promptKey = `${currentKey}-${state.queue.length}`

  if (item.type === 'pronunciation') {
    return (
      <div className="vocabulary-learning">
        <PronunciationPrompt
          key={promptKey}
          item={item}
          totalScore={totalScore}
          targetScore={state?.targetScore || 0}
          onSubmit={handleSubmit}
          onUseHint={handleUseHint}
          onNext={handleNext}
          submitting={submitting}
        />
      </div>
    )
  }

  return (
    <div className="vocabulary-learning">
      <VocabularyPrompt
        key={promptKey}
        vocabulary={item}
        totalScore={totalScore}
        targetScore={state?.targetScore || 0}
        onSubmit={handleSubmit}
        onUseHint={handleUseHint}
        onNext={handleNext}
        submitting={submitting}
      />
    </div>
  )
}
